import React from 'react';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { AlertTriangle, RefreshCw, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';

/**
 * Catches render-time JS errors in a route and shows a friendly fallback
 * instead of blanking the page. The technical error is toasted so the user
 * can copy/paste it when reporting, and we keep it in state for the details
 * disclosure.
 *
 * Usage:
 *   <RouteErrorBoundary routeName="Reports">
 *     <ReportsPage />
 *   </RouteErrorBoundary>
 */
export class RouteErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, showDetails: false };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    // Log full info to console for devs
    console.error(`[RouteErrorBoundary:${this.props.routeName || 'unknown'}]`, error, info);
    // Brief toast for the user — no stack, just the message
    const msg = error?.message || 'Unexpected error';
    try { toast.error(`Something went wrong: ${msg}`); } catch { /* sonner not mounted */ }
  }

  handleReload = () => {
    window.location.reload();
  };

  handleGoBack = () => {
    if (window.history.length > 1) {
      window.history.back();
    } else {
      window.location.href = '/dashboard';
    }
  };

  render() {
    if (!this.state.hasError) return this.props.children;

    const errMsg = this.state.error?.message || 'Unknown error';
    const errStack = this.state.error?.stack || '';

    return (
      <div className="min-h-[60vh] flex items-center justify-center p-6" data-testid="route-error-boundary">
        <Card className="p-8 max-w-lg w-full text-center">
          <AlertTriangle className="h-12 w-12 text-amber-500 mx-auto mb-4" />
          <h2 className="text-2xl font-heading font-semibold mb-2">Something went wrong</h2>
          <p className="text-muted-foreground mb-6">
            {this.props.routeName
              ? `The ${this.props.routeName} page hit an unexpected error.`
              : 'This page hit an unexpected error.'}{' '}
            Try reloading. If it keeps happening, please share the details below with support.
          </p>

          <div className="flex gap-2 justify-center mb-4">
            <Button onClick={this.handleReload} data-testid="error-boundary-reload">
              <RefreshCw className="mr-2 h-4 w-4" />
              Reload
            </Button>
            <Button variant="outline" onClick={this.handleGoBack} data-testid="error-boundary-back">
              <ArrowLeft className="mr-2 h-4 w-4" />
              Go Back
            </Button>
          </div>

          <button
            type="button"
            onClick={() => this.setState((s) => ({ showDetails: !s.showDetails }))}
            className="text-xs text-muted-foreground hover:text-foreground underline"
            data-testid="error-boundary-toggle-details"
          >
            {this.state.showDetails ? 'Hide' : 'Show'} technical details
          </button>

          {this.state.showDetails && (
            <pre
              className="mt-3 p-3 bg-muted rounded text-left text-xs overflow-auto max-h-48 whitespace-pre-wrap"
              data-testid="error-boundary-details"
            >
              {errMsg}
              {errStack ? `\n\n${errStack}` : ''}
            </pre>
          )}
        </Card>
      </div>
    );
  }
}
